import _ from 'lodash';
import flatten from 'flat';
import numeral from 'numeral';
import Raven from 'raven-js';
import { setAvailableLocales, updateTranslations } from '~/utils/i18n';
import { setApiUrl, updateClientColors } from '~/store/actions';
import { currentUser } from '~/store/getters';
import { PHP_CONFIG_PATH, DEFAULT_FAVICON_URL } from '~/utils/constants';
import { get as adapiGet } from '~/utils/adapi';
import { updateFavicon, updateTitle } from '~/utils/dom';
import { tryLoginFromCookie } from '~/utils/auth';
import Debug from '~/utils/debug';
import enTranslations from '@/locales/en.yml';

const debug = Debug('setup');

export const setupLocalization = (platform, languageCodes = [], commonStrings = {}, pConfig = {}) =>
  adapiGet(`config/locales?platform=${platform}`)
    .catch(error => {
      debug.error('Could not load localization: %o', error);
      return [];
    })
    .then(localeStrings => {
      const readyLocaleStrings = {};
      languageCodes.forEach(langCode => {
        readyLocaleStrings[langCode] = {
          ..._.cloneDeep(flatten(enTranslations)),
          ..._.pickBy(_.get(localeStrings, langCode, {}), (val, key) => !key.startsWith('_')),
          ..._.cloneDeep(commonStrings),
        };

        // Deletion is needed because you can't redefine locales normally
        // Tested with numeral 2.0.6
        // See also: https://github.com/adamwdraper/Numeral-js/issues/503
        delete numeral.locales[langCode];
        numeral.register('locale', langCode, {
          delimiters: {
            thousands: pConfig.delimiters.thousands,
            decimal: pConfig.delimiters.decimal,
          },
          abbreviations: {
            thousand: 'k',
            million: 'm',
            billion: 'b',
            trillion: 't',
          },
          currency: {
            symbol: pConfig.currency_symbol,
          },
        });
      });

      setAvailableLocales(languageCodes);
      return updateTranslations(readyLocaleStrings);
    })
    .catch(error => {
      debug.error('Error during setupLocalization: %o', error);
      Raven.captureException(error);
    });

export const setupClientConfig = (defaultApiUrl, shouldFetch) => Promise.resolve()
  .then(() => {
    if (shouldFetch) {
      return fetch(PHP_CONFIG_PATH).then(res => res.json());
    }
    return Promise.resolve({ FAM_API_URL: defaultApiUrl });
  })
  .then(clientConfig => {
    if (clientConfig.FAM_API_URL) {
      return setApiUrl(clientConfig.FAM_API_URL);
    }
    throw new Error(`clientConfig must contain FAM_API_URL: ${JSON.stringify(clientConfig)}`);
  });

// Returns logo
export const setupDesign = (config, defaultTitle = 'Title') => Promise.resolve()
  .then(() => updateFavicon(_.get(config, 'design.favicon', DEFAULT_FAVICON_URL)))
  .then(() => updateTitle(_.get(config, 'title', defaultTitle)))
  .then(() => updateClientColors(_.get(config, 'design.colors', {})))
  .then(() => _.get(config, 'design.logo', false));

export const setupAutologin = (currentPublic, currentPath, routerPush) => {
  const redirectToLogin = () => Promise.resolve()
    .then(() => routerPush({
      name: 'login',
      query: currentPath === '/' ? null : { next: currentPath },
    }));

  const tryLogin = () => tryLoginFromCookie()
    .catch(err => {
      debug.info('tryLoginFromCookie error: %o', err);
      return currentPublic ? Promise.resolve() : redirectToLogin();
    });

  return currentUser() ? Promise.resolve() : tryLogin();
};



// WEBPACK FOOTER //
// ./src/gotu-vue-common/src/utils/setup.js